import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Ticket, Search, Loader2, Mail, Clock, CheckCircle, User, X, MessageSquare, Send, ExternalLink, Trash2, Calendar, ArrowUpDown, Target, Phone } from 'lucide-react';

const AdminSupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState("ALL"); // ALL | SUPPORT | CAREER_ASSESSMENT
  const [dateFilterPreset, setDateFilterPreset] = useState("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [replyText, setReplyText] = useState("");
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [notification, setNotification] = useState(null);

  // Helper functions for Career Assessment tickets
  const isCareerAssessmentTicket = (t) => {
    if (!t) return false;
    const cat = (t.category || '').toLowerCase();
    const subj = (t.subject || '').toLowerCase();
    const msg = (t.message || '').toLowerCase();
    return cat === 'career_assessment' || cat === 'careerassessment' || subj.includes('career assessment') || msg.includes('career assessment');
  };

  const getAssessmentDate = (t) => {
    if (!t) return '';
    if (t.assessment_date && String(t.assessment_date).trim()) return String(t.assessment_date).trim();
    const match = t.message?.match(/(?:Selected Assessment Date|Scheduled Date|Date):\s*([^\n\r,]+)/i);
    return match ? match[1].trim() : '';
  };

  const getCandidatePhone = (t) => {
    if (!t || !t.message) return '';
    const match = t.message.match(/Phone:\s*([^\n\r]+)/i);
    return match ? match[1].trim() : '';
  };

  // Live Chat Box state
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const messagesEndRef = React.useRef(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = (currentUser?.role || 'ADMIN').toUpperCase();
  const isAdmin = userRole === 'ADMIN' || !currentUser?.role;
  const canDeleteTicket = isAdmin || userRole === 'INSTRUCTOR';

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedTicket) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedTicket]);

  const fetchTickets = async () => {
    setIsLoading(true);
    try {
      const response = await fetch((window.API_BASE || '') + '/api/admin/tickets', {
        headers: { 'ngrok-skip-browser-warning': 'true' }
      });
      const data = await response.json();
      if (data.success) {
        setTickets(data.tickets);
      }
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchChatMessages = async (ticketId, silent = false) => {
    if (!silent) setIsChatLoading(true);
    try {
      const response = await fetch((window.API_BASE || '') + `/api/tickets/${ticketId}/messages`, {
        headers: { 'ngrok-skip-browser-warning': 'true' }
      });
      const data = await response.json();
      if (data.success) {
        setChatMessages(data.messages || []);
        if (data.ticket) {
          setSelectedTicket(data.ticket);
        }
      }
    } catch (err) {
      console.error('Failed to fetch ticket chat:', err);
    } finally {
      if (!silent) setIsChatLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedTicket?.id) return;
    fetchChatMessages(selectedTicket.id, false);

    const interval = setInterval(() => {
      fetchChatMessages(selectedTicket.id, true);
    }, 3000);

    return () => clearInterval(interval);
  }, [selectedTicket?.id]);

  useEffect(() => {
    if (selectedTicket) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, selectedTicket]);

  const updateStatus = async (id, newStatus) => {
    try {
      const response = await fetch((window.API_BASE || "") + `/api/admin/tickets/${id}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await response.json();
      if (data.success) {
        setTickets(tickets.map(t => t.id === id ? { ...t, status: newStatus } : t));
        if (selectedTicket && selectedTicket.id === id) {
          setSelectedTicket({ ...selectedTicket, status: newStatus });
        }
      }
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const handleDeleteTicket = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this support ticket? This action cannot be undone.")) {
      return;
    }

    try {
      const response = await fetch((window.API_BASE || '') + `/api/admin/tickets/${id}`, {
        method: 'DELETE',
        headers: { 'ngrok-skip-browser-warning': 'true' }
      });
      const data = await response.json();
      if (data.success) {
        setTickets(prev => prev.filter(t => t.id !== id));
        if (selectedTicket && selectedTicket.id === id) {
          setSelectedTicket(null);
        }
      } else {
        alert(data.message || 'Failed to delete ticket');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error deleting support ticket');
    }
  };

  const handleSelectAllTickets = (e) => {
    if (e.target.checked) {
      setSelectedTicketIds(filteredTickets.map(t => t.id));
    } else {
      setSelectedTicketIds([]);
    }
  };

  const handleSelectTicket = (id, e) => {
    if (e) e.stopPropagation();
    if (selectedTicketIds.includes(id)) {
      setSelectedTicketIds(selectedTicketIds.filter(i => i !== id));
    } else {
      setSelectedTicketIds([...selectedTicketIds, id]);
    }
  };

  const handleBulkDeleteTickets = async () => {
    if (!isAdmin) {
      alert('Only administrators have access to bulk delete entries.');
      return;
    }
    if (selectedTicketIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedTicketIds.length} selected ticket(s)? This action cannot be undone.`)) return;

    try {
      const response = await fetch((window.API_BASE || '') + '/api/admin/tickets/bulk-delete', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true' 
        },
        body: JSON.stringify({ ids: selectedTicketIds })
      });
      const data = await response.json();
      if (data.success) {
        setTickets(tickets.filter(t => !selectedTicketIds.includes(t.id)));
        setSelectedTicketIds([]);
      } else {
        alert(data.message || 'Bulk delete failed');
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
      setTickets(tickets.filter(t => !selectedTicketIds.includes(t.id)));
      setSelectedTicketIds([]);
    }
  };

  const handleSendReply = async (newStatus = 'RESOLVED') => {
    if (!replyText.trim() || !selectedTicket) {
      setNotification({ type: 'error', text: 'Please enter a reply message before sending.' });
      return;
    }

    setIsSendingReply(true);
    setNotification(null);
    const messageText = replyText.trim();
    setReplyText("");

    try {
      const response = await fetch((window.API_BASE || "") + `/api/tickets/${selectedTicket.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify({
          sender_type: currentUser.role || 'ADMIN',
          sender_name: currentUser.name || 'Support Staff',
          sender_id: currentUser.id,
          message: messageText,
          status: newStatus
        })
      });

      const data = await response.json();

      if (data.success) {
        fetchChatMessages(selectedTicket.id, true);
        fetchTickets();
        setNotification({ type: 'success', text: 'Message posted to Chat Box!' });
      } else {
        setNotification({ type: 'error', text: data.message || 'Failed to send message.' });
      }
    } catch (err) {
      console.error('Reply send error:', err);
      setNotification({ type: 'error', text: 'Network error while sending reply.' });
    } finally {
      setIsSendingReply(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (selectedTicket) {
      setNotification(null);
      setReplyText("");
    }
  }, [selectedTicket?.id]);

  const isDateInRange = (dateStr) => {
    if (dateFilterPreset === 'ALL' && !startDate && !endDate) return true;
    if (!dateStr) return false;

    const ticketDate = new Date(dateStr);
    if (isNaN(ticketDate.getTime())) return false;

    const now = new Date();

    if (dateFilterPreset === 'TODAY') {
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      return ticketDate >= todayStart && ticketDate <= todayEnd;
    }

    if (dateFilterPreset === 'YESTERDAY') {
      const yestStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
      const yestEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999);
      return ticketDate >= yestStart && ticketDate <= yestEnd;
    }

    if (dateFilterPreset === 'LAST_7_DAYS') {
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return ticketDate >= sevenDaysAgo && ticketDate <= now;
    }

    if (dateFilterPreset === 'LAST_30_DAYS') {
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return ticketDate >= thirtyDaysAgo && ticketDate <= now;
    }

    if (dateFilterPreset === 'THIS_MONTH') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      return ticketDate >= monthStart && ticketDate <= now;
    }

    // Custom date range (or manual dates)
    if (startDate) {
      const start = new Date(startDate + "T00:00:00");
      if (ticketDate < start) return false;
    }

    if (endDate) {
      const end = new Date(endDate + "T23:59:59.999");
      if (ticketDate > end) return false;
    }

    return true;
  };

  const handleClearDateFilter = () => {
    setDateFilterPreset("ALL");
    setStartDate("");
    setEndDate("");
  };

  const careerAssessmentCount = tickets.filter(t => isCareerAssessmentTicket(t)).length;
  const generalSupportCount = tickets.filter(t => !isCareerAssessmentTicket(t)).length;
  const totalCount = tickets.length;

  const filteredTickets = tickets
    .filter(ticket => {
      // Category filter: when CAREER_ASSESSMENT is selected, show ONLY career assessment tickets
      if (categoryFilter === 'CAREER_ASSESSMENT' && !isCareerAssessmentTicket(ticket)) {
        return false;
      }
      if (categoryFilter === 'SUPPORT' && isCareerAssessmentTicket(ticket)) {
        return false;
      }

      const assessmentDateStr = getAssessmentDate(ticket);
      const phoneStr = getCandidatePhone(ticket);

      const matchesSearch = (
        ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ticket.name && ticket.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (ticket.email && ticket.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (ticket.message && ticket.message.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (assessmentDateStr && assessmentDateStr.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (phoneStr && phoneStr.includes(searchTerm))
      );

      const matchesStatus = statusFilter === "ALL" || ticket.status === statusFilter;
      const matchesDate = isDateInRange(ticket.created_at);

      return matchesSearch && matchesStatus && matchesDate;
    })
    .sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return sortOrder === 'DESC' ? dateB - dateA : dateA - dateB;
    });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH': return 'text-rose-500 bg-rose-50';
      case 'MEDIUM': return 'text-amber-500 bg-amber-50';
      default: return 'text-blue-500 bg-blue-50';
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-[1600px] mx-auto">
      {/* Ticket Detail & Email Reply Modal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {selectedTicket && (
            <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4">
              <motion.div 
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setSelectedTicket(null)}
                className="absolute inset-0 bg-slate-950/75 backdrop-blur-md"
              />
              <motion.div 
                initial={{ opacity: 0, scale: 0.98, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98, y: 10 }}
                className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200/60 max-h-[90vh] flex flex-col z-10"
              >
                {/* Modal Header */}
                <div className="p-8 pb-4 border-b border-slate-100 flex justify-between items-start shrink-0 bg-slate-50/50">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`w-fit px-3 py-1 rounded-full text-[9px] font-medium tracking-widest uppercase ${getPriorityColor(selectedTicket.priority)}`}>
                        {selectedTicket.priority} Priority
                      </span>
                      {isCareerAssessmentTicket(selectedTicket) && (
                        <span className="px-3 py-1 rounded-full text-[9px] font-bold tracking-widest uppercase bg-purple-100 text-purple-700 flex items-center gap-1">
                          <Target size={10} /> Career Assessment
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-[9px] font-medium tracking-widest uppercase ${
                        selectedTicket.status === 'OPEN' ? 'bg-blue-100 text-blue-600' : 
                        selectedTicket.status === 'IN_PROGRESS' ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'
                      }`}>
                        {selectedTicket.status}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900 leading-snug">{selectedTicket.subject}</h2>
                  </div>
                  <div className="flex items-center gap-1">
                    {canDeleteTicket && (
                      <button 
                        onClick={(e) => handleDeleteTicket(selectedTicket.id, e)}
                        className="p-2 hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded-full transition-colors"
                        title="Delete Support Ticket"
                      >
                        <Trash2 size={20} />
                      </button>
                    )}
                    <button onClick={() => setSelectedTicket(null)} className="p-2 hover:bg-slate-200/50 rounded-full text-slate-400 transition-colors">
                      <X size={22} />
                    </button>
                  </div>
                </div>

                {/* Scrollable Content */}
                <div className="p-6 overflow-y-auto space-y-6 flex-1 bg-slate-50/40">
                  {/* Career Assessment Highlight Banner */}
                  {isCareerAssessmentTicket(selectedTicket) && (
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50/80 border border-purple-200/90 rounded-2xl flex flex-wrap items-center justify-between gap-3 shadow-xs">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center font-bold shadow-xs shrink-0">
                          <Target size={20} />
                        </div>
                        <div>
                          <span className="text-[10px] font-black uppercase tracking-wider text-purple-700 bg-purple-200/70 px-2 py-0.5 rounded">
                            Career Assessment Candidate
                          </span>
                          <p className="text-xs text-slate-800 font-bold mt-1 flex flex-wrap items-center gap-2">
                            <span>{selectedTicket.name || 'Anonymous'}</span>
                            <span className="text-slate-300">•</span>
                            <span className="text-slate-600 font-normal">{selectedTicket.email}</span>
                            {getCandidatePhone(selectedTicket) && (
                              <>
                                <span className="text-slate-300">•</span>
                                <span className="text-purple-700 font-semibold flex items-center gap-1">
                                  <Phone size={10} /> {getCandidatePhone(selectedTicket)}
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="bg-white border border-purple-200 px-4 py-2 rounded-xl text-right shadow-2xs shrink-0">
                        <p className="text-[9px] font-bold text-purple-600 uppercase tracking-tight">Scheduled Assessment Date</p>
                        <p className="text-sm font-black text-purple-950 flex items-center gap-1.5 justify-end">
                          <Calendar size={14} className="text-purple-600" />
                          {getAssessmentDate(selectedTicket) || 'Not specified'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* User Info Bar */}
                  <div className="flex items-center justify-between gap-3 p-3 bg-white rounded-xl border border-slate-200/80 shadow-2xs">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 shrink-0 font-bold text-xs">
                        <User size={16} />
                      </div>
                      <div className="overflow-hidden">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">Student Name</p>
                        <p className="text-xs font-bold text-slate-900 truncate">{selectedTicket.name || 'Anonymous'}</p>
                      </div>
                    </div>
                    {selectedTicket.created_at && (
                      <div className="text-right shrink-0">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">Submitted On</p>
                        <p className="text-xs font-semibold text-slate-700 flex items-center gap-1 justify-end">
                          <Calendar size={12} className="text-brand-600" />
                          {new Date(selectedTicket.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Live Chat Box Thread */}
                  <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs">
                    <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                      <MessageSquare size={14} className="text-brand-600" /> Live Chat Thread
                    </h4>

                    <div className="space-y-4 max-h-[350px] overflow-y-auto pr-1">
                      {isChatLoading && chatMessages.length === 0 ? (
                        <div className="flex justify-center p-6"><Loader2 className="animate-spin text-brand-600" size={20} /></div>
                      ) : chatMessages.length === 0 ? (
                        <div className="text-xs text-slate-400 italic text-center py-4 font-medium">"{selectedTicket.message}"</div>
                      ) : (
                        chatMessages.map((msg, index) => {
                          const isStudent = msg.sender_type === 'USER';
                          return (
                            <div key={msg.id || index} className={`flex flex-col ${isStudent ? 'items-start' : 'items-end'}`}>
                              <div className="flex items-center gap-2 mb-1 px-1">
                                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                                  {isStudent ? (msg.sender_name || 'Student') : (msg.sender_name || 'Support / Instructor')}
                                </span>
                                <span className="text-[9px] text-slate-400">
                                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              <div 
                                className={`min-w-[70px] max-w-[85%] px-4 py-2.5 rounded-2xl text-xs font-medium leading-relaxed whitespace-pre-wrap ${
                                  isStudent 
                                    ? 'bg-slate-100 text-slate-800 rounded-tl-xs border border-slate-200/60 text-left' 
                                    : 'bg-blue-600 text-white rounded-tr-xs shadow-xs text-right'
                                }`}
                              >
                                {msg.message}
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </div>

                  {/* Reply Form Section */}
                  <div className="bg-slate-900 text-white rounded-2xl p-5 shadow-xl">
                    {selectedTicket && (selectedTicket.status?.toUpperCase() === 'RESOLVED' || selectedTicket.status?.toUpperCase() === 'CLOSED') && (
                      <div className="mb-3 p-2.5 bg-emerald-500/15 border border-emerald-500/30 rounded-xl flex items-center justify-between text-xs text-emerald-300">
                        <div className="flex items-center gap-2 font-bold">
                          <CheckCircle size={14} className="text-emerald-400" /> Ticket currently marked as Resolved
                        </div>
                        <span className="text-[10px] text-slate-400 font-medium">Replying will send to student chat</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-200 flex items-center gap-2">
                        <Send size={14} className="text-blue-400" /> Send Response in Chat Box
                      </h4>
                      <span className="text-[10px] font-medium text-slate-400">Live Chat</span>
                    </div>

                      <textarea 
                        rows="3"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Type your response here to send into the chat box..."
                        className="w-full p-3.5 text-xs bg-slate-800 border border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none font-medium text-slate-100 placeholder-slate-400"
                      />

                      {/* Notification Feedback */}
                      {notification && (
                        <div className={`mt-3 p-2.5 text-xs rounded-xl font-medium flex items-center gap-2 ${
                          notification.type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                        }`}>
                          {notification.type === 'success' ? <CheckCircle size={14} /> : <X size={14} />}
                          {notification.text}
                        </div>
                      )}

                      <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-800">
                        <span className="text-[10px] text-slate-400 italic">
                          Live Chat Conversation
                        </span>
                        <div className="flex gap-2">
                          <button 
                            disabled={isSendingReply || !replyText.trim()}
                            onClick={() => handleSendReply('IN_PROGRESS')}
                            className="px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 text-xs font-bold rounded-xl transition-all disabled:opacity-40 flex items-center gap-1.5 active:scale-95"
                          >
                            {isSendingReply ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                            Post & In Progress
                          </button>

                          <button 
                            disabled={isSendingReply || !replyText.trim()}
                            onClick={() => handleSendReply('RESOLVED')}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-40 flex items-center gap-1.5 active:scale-95"
                          >
                            {isSendingReply ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                            Post & Resolve
                          </button>
                        </div>
                      </div>
                    </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-1">Student Support & Bookings</h1>
          <p className="text-slate-500 font-medium text-xs">Review general inquiries and candidate Career Assessment scheduled bookings.</p>
        </div>

        {/* Category Tabs: All | General Support | Career Assessment */}
        <div className="flex items-center gap-1.5 bg-slate-200/60 p-1.5 rounded-2xl border border-slate-200 shadow-2xs">
          <button
            onClick={() => setCategoryFilter('ALL')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all ${
              categoryFilter === 'ALL'
                ? 'bg-white text-slate-900 shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
            }`}
          >
            <span>All Tickets</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
              categoryFilter === 'ALL' ? 'bg-slate-100 text-slate-800' : 'bg-slate-200/80 text-slate-600'
            }`}>
              {totalCount}
            </span>
          </button>

          <button
            onClick={() => setCategoryFilter('SUPPORT')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all ${
              categoryFilter === 'SUPPORT'
                ? 'bg-blue-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
            }`}
          >
            <Ticket size={13} />
            <span>General Support</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
              categoryFilter === 'SUPPORT' ? 'bg-blue-500 text-white' : 'bg-slate-200/80 text-slate-600'
            }`}>
              {generalSupportCount}
            </span>
          </button>

          <button
            onClick={() => setCategoryFilter('CAREER_ASSESSMENT')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              categoryFilter === 'CAREER_ASSESSMENT'
                ? 'bg-purple-600 text-white shadow-md shadow-purple-200 ring-2 ring-purple-400/30'
                : 'text-purple-700 bg-purple-50 hover:bg-purple-100 hover:text-purple-900 border border-purple-200/80'
            }`}
          >
            <Target size={14} className={categoryFilter === 'CAREER_ASSESSMENT' ? 'text-purple-200' : 'text-purple-600'} />
            <span>Career Assessment</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
              categoryFilter === 'CAREER_ASSESSMENT' ? 'bg-purple-500 text-white' : 'bg-purple-200 text-purple-800'
            }`}>
              {careerAssessmentCount}
            </span>
          </button>
        </div>
      </div>

      <div className="glass-card rounded-2xl border-slate-200/60 shadow-[0_12px_24px_rgba(0,0,0,0.03)] overflow-hidden">
        {/* Search & Filter Bar */}
        <div className="p-8 border-b border-slate-50 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-[280px]">
            <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-all text-xs font-semibold text-slate-700">
              <input 
                type="checkbox"
                className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 w-4 h-4 cursor-pointer"
                checked={filteredTickets.length > 0 && selectedTicketIds.length === filteredTickets.length}
                onChange={handleSelectAllTickets}
              />
              Select All
            </label>
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input 
                className="w-full pl-12 pr-6 py-3 bg-slate-50/50 border border-slate-100 rounded-xl text-xs focus:outline-none focus:ring-4 focus:ring-brand-600/5 focus:border-brand-300 transition-all font-medium" 
                placeholder="Search by query, name, or email..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {isAdmin && selectedTicketIds.length > 0 && (
              <button
                onClick={handleBulkDeleteTickets}
                className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-all"
              >
                <Trash2 size={14} />
                Delete Selected ({selectedTicketIds.length})
              </button>
            )}
          </div>

          {/* Status Filters */}
          <div className="flex items-center gap-2 bg-slate-100/70 p-1 rounded-xl">
            {['ALL', 'OPEN', 'IN_PROGRESS', 'RESOLVED'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                  statusFilter === status 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {status.replace('_', ' ')}
              </button>
            ))}
          </div>

          {isLoading && <Loader2 className="animate-spin text-brand-600" size={18} />}
        </div>

        {/* Date Filter & Sort Toolbar */}
        <div className="px-8 py-3.5 bg-slate-50/75 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-slate-500 font-semibold mr-1">
              <Calendar size={14} className="text-brand-600" />
              <span>Date:</span>
            </div>

            {/* Presets */}
            <div className="flex flex-wrap items-center gap-1 bg-white border border-slate-200/90 p-1 rounded-xl shadow-2xs">
              {[
                { key: 'ALL', label: 'All Dates' },
                { key: 'TODAY', label: 'Today' },
                { key: 'YESTERDAY', label: 'Yesterday' },
                { key: 'LAST_7_DAYS', label: 'Last 7 Days' },
                { key: 'LAST_30_DAYS', label: 'Last 30 Days' },
                { key: 'THIS_MONTH', label: 'This Month' },
                { key: 'CUSTOM', label: 'Custom Range' },
              ].map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => {
                    setDateFilterPreset(preset.key);
                    if (preset.key !== 'CUSTOM') {
                      setStartDate('');
                      setEndDate('');
                    }
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    dateFilterPreset === preset.key
                      ? 'bg-brand-600 text-white shadow-xs'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Custom Range Inputs */}
            {dateFilterPreset === 'CUSTOM' && (
              <div className="flex items-center gap-2 bg-white border border-slate-200/90 px-3 py-1.5 rounded-xl shadow-2xs">
                <label className="text-[10px] uppercase font-bold text-slate-400">From</label>
                <input 
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="text-xs text-slate-700 bg-transparent focus:outline-none border-b border-slate-200 focus:border-brand-500 py-0.5"
                />
                <label className="text-[10px] uppercase font-bold text-slate-400 ml-1">To</label>
                <input 
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="text-xs text-slate-700 bg-transparent focus:outline-none border-b border-slate-200 focus:border-brand-500 py-0.5"
                />
              </div>
            )}

            {/* Clear Button */}
            {(dateFilterPreset !== 'ALL' || startDate || endDate) && (
              <button
                onClick={handleClearDateFilter}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200/60 rounded-xl transition-colors cursor-pointer"
                title="Reset date filter"
              >
                <X size={12} /> Clear Date
              </button>
            )}
          </div>

          {/* Right Controls: Sort Order & Count */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 bg-white border border-slate-200/90 px-3 py-1.5 rounded-xl shadow-2xs">
              <ArrowUpDown size={13} className="text-slate-400" />
              <span className="text-xs text-slate-500 font-medium">Sort:</span>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="bg-transparent text-xs font-bold text-slate-700 focus:outline-none cursor-pointer"
              >
                <option value="DESC">Newest First</option>
                <option value="ASC">Oldest First</option>
              </select>
            </div>

            <span className="text-xs font-semibold text-slate-500 bg-white border border-slate-200/80 px-3 py-1.5 rounded-xl shadow-2xs">
              Showing <span className="font-bold text-slate-800">{filteredTickets.length}</span> {filteredTickets.length === 1 ? 'ticket' : 'tickets'}
            </span>
          </div>
        </div>

        {/* Tickets Grid */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredTickets.map((ticket) => (
              <motion.div 
                key={ticket.id}
                layout
                onClick={() => setSelectedTicket(ticket)}
                className="bg-slate-50/50 border border-slate-100 rounded-2xl p-6 hover:bg-white hover:shadow-md transition-all group cursor-pointer relative flex flex-col justify-between"
              >
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox"
                        className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 w-4 h-4 cursor-pointer"
                        checked={selectedTicketIds.includes(ticket.id)}
                        onChange={(e) => handleSelectTicket(ticket.id, e)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className={`px-3 py-1 rounded-full text-[9px] font-medium tracking-widest uppercase ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority} Priority
                      </div>
                      {isCareerAssessmentTicket(ticket) && (
                        <span className="px-2.5 py-1 rounded-full text-[9px] font-extrabold bg-purple-100 text-purple-700 border border-purple-200/80 flex items-center gap-1 shadow-2xs">
                          <Target size={10} /> Career Assessment
                        </span>
                      )}
                      {ticket.admin_reply && (
                        <span className="px-2.5 py-1 rounded-full text-[9px] font-semibold bg-indigo-50 text-indigo-600 flex items-center gap-1">
                          <Mail size={10} /> Replied
                        </span>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-[9px] font-medium tracking-widest uppercase ${
                      ticket.status === 'OPEN' ? 'bg-blue-100 text-blue-600' : 
                      ticket.status === 'IN_PROGRESS' ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'
                    }`}>
                      {ticket.status}
                    </span>
                  </div>

                  <h4 className="text-base font-semibold text-slate-900 mb-1 truncate">{ticket.subject}</h4>

                  {/* Scheduled Assessment Date Callout for Career Assessment */}
                  {isCareerAssessmentTicket(ticket) && (
                    <div className="my-3 px-3 py-2 bg-gradient-to-r from-purple-50 to-indigo-50/70 border border-purple-200/90 rounded-xl flex flex-wrap items-center justify-between gap-2 shadow-2xs">
                      <div className="flex items-center gap-2 text-purple-900 text-xs font-bold">
                        <Calendar size={13} className="text-purple-600 shrink-0" />
                        <span className="text-[10px] uppercase tracking-wider text-purple-600 font-extrabold">Assessment Date:</span>
                        <span className="text-purple-950 font-black bg-white px-2 py-0.5 rounded-md border border-purple-200 text-xs">
                          {getAssessmentDate(ticket) || 'Not specified'}
                        </span>
                      </div>
                      {getCandidatePhone(ticket) && (
                        <span className="text-[11px] font-semibold text-purple-800 flex items-center gap-1 bg-white/80 px-2 py-0.5 rounded-md border border-purple-100">
                          <Phone size={10} className="text-purple-500" /> {getCandidatePhone(ticket)}
                        </span>
                      )}
                    </div>
                  )}

                  <p className="text-xs text-slate-500 mb-6 line-clamp-2 italic leading-relaxed break-words">"{ticket.message}"</p>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-medium text-slate-600 flex items-center gap-1">
                      <User size={12} className="text-slate-400" /> {ticket.name || 'Anonymous Student'}
                    </span>
                    <span className="text-[10px] text-slate-400 opacity-80 flex items-center gap-1 mt-0.5">
                      <Mail size={10} /> {ticket.email}
                    </span>
                    {ticket.created_at && (
                      <span className="text-[10px] text-slate-500 font-medium flex items-center gap-1 mt-1 bg-slate-100/90 px-2 py-0.5 rounded-md w-fit">
                        <Calendar size={11} className="text-brand-600" />
                        {new Date(ticket.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTicket(ticket);
                      }}
                      className="px-3 py-1.5 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors flex items-center gap-1"
                      title="Reply via Email"
                    >
                      <Mail size={14} /> Answer
                    </button>
                    {ticket.status === 'OPEN' && (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          updateStatus(ticket.id, 'IN_PROGRESS');
                        }}
                        className="p-2 text-amber-500 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors"
                        title="Mark In Progress"
                      >
                        <Clock size={16} />
                      </button>
                    )}
                    {ticket.status !== 'RESOLVED' && (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          updateStatus(ticket.id, 'RESOLVED');
                        }}
                        className="p-2 text-emerald-500 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors"
                        title="Mark Resolved"
                      >
                        <CheckCircle size={16} />
                      </button>
                    )}
                    {canDeleteTicket && (
                      <button 
                        onClick={(e) => handleDeleteTicket(ticket.id, e)}
                        className="p-2 text-rose-500 bg-rose-50 rounded-xl hover:bg-rose-100 transition-colors"
                        title="Delete Support Ticket"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {!isLoading && filteredTickets.length === 0 && (
            <div className="p-20 text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Ticket className="text-blue-600" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">
                {categoryFilter === 'CAREER_ASSESSMENT' ? 'No Career Assessment bookings match your filters' : 'No tickets match your filters'}
              </h3>
              <p className="text-slate-400 text-sm italic mb-4">
                {categoryFilter === 'CAREER_ASSESSMENT' ? 'Try adjusting your date range, search query, or check All Tickets.' : 'Try adjusting your date range, search query, or status filter.'}
              </p>
              {(dateFilterPreset !== 'ALL' || startDate || endDate || searchTerm || statusFilter !== 'ALL' || categoryFilter !== 'ALL') && (
                <button
                  onClick={() => {
                    handleClearDateFilter();
                    setSearchTerm('');
                    setStatusFilter('ALL');
                    setCategoryFilter('ALL');
                  }}
                  className="px-4 py-2 bg-brand-50 text-brand-600 hover:bg-brand-100 rounded-xl text-xs font-bold transition-all"
                >
                  Reset All Filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AdminSupportTickets;
